import { formatoEuros } from '../api';
import { Cabecera, Vacio } from '../componentes';
import { useCola } from '../hooks';
import { descartar, type Accion } from '../offline/cola';

const NOMBRES: Record<Accion['tipo'], string> = {
  'crear-nota': 'Crear la nota',
  'crear-gasto': 'Guardar el gasto',
  'subir-foto': 'Subir la foto del ticket',
  'enviar-nota': 'Enviar la nota a aprobar',
  'borrar-nota': 'Borrar la nota',
};

/**
 * Qué está esperando y qué se ha atascado.
 *
 * Es la pantalla que evita la peor conversación posible: "no sé si mis tickets
 * se han subido". Aquí se ve qué falta, por qué, y se puede hacer algo.
 */
export function Pendientes({ alVolver }: { alVolver: () => void }) {
  const cola = useCola();

  const enCola = cola.acciones
    .filter((a) => !a.irrecuperable)
    .sort((a, b) => a.orden - b.orden);
  const atascadas = cola.acciones.filter((a) => a.irrecuperable);

  return (
    <>
      <Cabecera titulo="Pendiente de subir" alVolver={alVolver} />

      <div className="contenido">
        {cola.pausadaPorClave && (
          <p className="aviso atencion">
            Tu cuenta de Gextia necesita volver a conectarse. Nada se ha perdido:
            en cuanto la reconectes, todo esto sube solo.
          </p>
        )}

        {enCola.length === 0 && atascadas.length === 0 && (
          <Vacio titulo="No queda nada por subir." sugerencia="Todo está en Gextia." />
        )}

        {enCola.length > 0 && (
          <>
            <h2 style={{ fontSize: '0.95rem', margin: '0.5rem 0 0' }}>En cola</h2>
            <ul className="lista">
              {enCola.map((accion) => (
                <li key={accion.id} className="tarjeta">
                  <div className="tarjeta-fila">
                    <span className="tarjeta-titulo">{NOMBRES[accion.tipo]}</span>
                    <span className="marca espera">
                      {accion.estado === 'enviando' ? 'Subiendo…' : 'Esperando'}
                    </span>
                  </div>
                  <div className="tarjeta-meta">{describir(accion)}</div>
                </li>
              ))}
            </ul>
          </>
        )}

        {atascadas.length > 0 && (
          <>
            <h2 style={{ fontSize: '0.95rem', margin: '1rem 0 0' }}>Necesitan que decidas</h2>
            <ul className="lista">
              {atascadas.map((accion) => (
                <li key={accion.id} className="tarjeta">
                  <div className="tarjeta-fila">
                    <span className="tarjeta-titulo">{NOMBRES[accion.tipo]}</span>
                    <span className="marca fallo">No subió</span>
                  </div>
                  <div className="tarjeta-meta">{describir(accion)}</div>
                  {accion.ultimoError && (
                    <p className="aviso error" style={{ marginTop: '0.6rem' }}>
                      {accion.ultimoError}
                    </p>
                  )}
                  <div className="tarjeta-fila" style={{ marginTop: '0.5rem' }}>
                    <span className="tarjeta-meta">
                      {accion.intentos} {accion.intentos === 1 ? 'intento' : 'intentos'}
                    </span>
                    <button
                      type="button"
                      className="boton peligro"
                      onClick={() => void descartar(accion.id)}
                    >
                      Descartar
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            <p className="tarjeta-meta">
              Descartar borra solo eso del móvil. Lo que ya subió a Gextia se queda.
            </p>
          </>
        )}
      </div>

      {(enCola.length > 0 || atascadas.length > 0) && (
        <div className="acciones-abajo">
          <button type="button" className="boton ancho" onClick={cola.reintentar}>
            Reintentar ahora
          </button>
        </div>
      )}
    </>
  );
}

function describir(accion: Accion): string {
  switch (accion.tipo) {
    case 'crear-nota':
      return String(accion.datos.nombre ?? '');
    case 'crear-gasto':
      return `${accion.datos.concepto} · ${formatoEuros(Number(accion.datos.importe) || 0)}`;
    case 'subir-foto':
      return String(accion.datos.nombre ?? 'ticket.jpg');
    case 'enviar-nota':
      return 'Última acción de la nota';
    case 'borrar-nota':
      return 'Se borrará también en Gextia';
  }
}
